/**
 * Ouverture d'une page sur le site d'une autre marque (docs/ux-v2/SPEC.md règle 4, PL11 ; retour de Luc du 04/10) :
 * on ne réserve un cours que sur le site de sa marque, dans un NOUVEL ONGLET. Toute ouverture inter-sites de la v2
 * passe par openCrossSite (cours d'une autre marque du planning, Run Club des sites RIISE / Punch vers Dynamo).
 *
 * L'onglet est ouvert de façon synchrone, pendant le clic (`about:blank`) : un `window.open` fait après un `await`
 * n'est plus rattaché au geste de l'utilisateur et serait bloqué (Safari, Firefox). L'adresse finale, éventuellement
 * complétée par decorateCrossSiteUrl (passage de session, ./handoff.ts), y est chargée ensuite. L'onglet perd son
 * lien vers la page d'origine (`opener` vidé, comme rel="noopener").
 */
import { decorateCrossSiteUrl } from './handoff';

/** Adresse http(s) absolue, sinon null (jamais de `javascript:` ou d'adresse relative chargée dans l'onglet). */
export function absoluteHttpUrl(url: unknown): string | null {
  if (typeof url !== 'string' || url.trim() === '') return null;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** Nouvel onglet : `replace`, pour que « précédent » n'y ramène pas sur une page blanche. */
function load(tab: Window, url: string) {
  try {
    tab.location.replace(url);
  } catch {
    tab.location.href = url;
  }
}

/**
 * Ouvre `href` (page du site `targetSiteId`) dans un nouvel onglet.
 *
 * @returns l'onglet ouvert, ou null si le navigateur a refusé l'ouverture : l'adresse est alors chargée dans l'onglet
 *          courant (le bouton « précédent » ramène au planning), pour que le visiteur puisse quand même réserver.
 */
export function openCrossSite(href: string, targetSiteId: string): Window | null {
  const raw = absoluteHttpUrl(href);
  if (!raw) return null;

  let tab: Window | null = null;
  try {
    tab = window.open('about:blank', '_blank');
  } catch {
    tab = null;
  }
  if (tab) {
    try {
      tab.opener = null;
    } catch {
      // Navigateur qui refuse : sans conséquence, la page chargée ensuite est d'une autre origine.
    }
  }

  decorateCrossSiteUrl(raw, targetSiteId)
    .then((decorated) => absoluteHttpUrl(decorated) ?? raw)
    .catch(() => raw)
    .then((url) => (tab ? load(tab, url) : window.location.assign(url)));

  return tab;
}
